//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import angular, { ILogService, IQService, IRootScopeService, ITimeoutService, IWindowService } from 'angular';
import { IModalInstanceService, IModalService } from 'angular-ui-bootstrap';
import PhotoEditorSDKReactUI from 'photoeditorsdk/js/PhotoEditorSDK.UI.ReactUI';
import _ from 'underscore';

import 'photoeditorsdk/css/PhotoEditorSDK.UI.ReactUI.min.css';

import { GaApi } from 'app/initialization/ga/ga-api';
import { TRACKING_ACTION } from '@tracker/trackerModel';

import './BeautifyActionButton';
import { BeautifyConfig } from './beautifyConfig';
import instagramPhotoEditorConfig from './instagram-photo-editor-config';
import './photo-editor-beautify-button.less';
import photoEditorConfig from './photo-editor-config';

export class PhotoEditorBeautifyButton {
  public isLoading: boolean = true;
  public BeautifyConfig = BeautifyConfig;

  // Bindings
  public disabled: boolean;
  public config?: BeautifyConfig;
  private imageUrl: string;
  private fileId: string;
  private saveClicked;
  private uiaLaunchContext: string;
  private beautifyClickedCallback?: () => void;

  // Local vars
  private modalInstance?: IModalInstanceService = null;
  private editor = null;
  private imageToLoad?: HTMLImageElement = null;
  private photoEditorElemId = 'photo-editor';

  constructor(
    private $log: ILogService,
    private $modal: IModalService,
    private $q: IQService,
    private $rootScope: IRootScopeService,
    private $timeout: ITimeoutService,
    private $window: IWindowService,
    private imageService,
    private trackingService: GaApi,
  ) { }

  public $onChanges(obj) {
    const imageUrlChange = obj?.imageUrl?.currentValue;

    if (imageUrlChange) {
      this.isLoading = true;

      const initialImagePromise = this.fileId ?
        this.imageService.getBeautifyImage(this.fileId) : this.$q.when({ imageUrl: this.imageUrl });

      initialImagePromise.then((response) => {
        return response.imageUrl;
      }, (error) => {
        this.$log.error(`Failed to generate beautified image url. (${error.code})${error.message}`);
        return this.imageUrl;
      }).then((imageUrl) => {
        // The SDK will fail if the URL does not have a valid protocol before it
        if (imageUrl.indexOf('//') === 0) {
          imageUrl = this.$window.location.protocol + imageUrl;
        }
        return imageUrl;
      }).then((imageUrl) => {
        this.preloadImage(imageUrl);
      });
    }
  }

  public beautifyClicked() {
    if (this.beautifyClickedCallback) {
      this.beautifyClickedCallback();
    }

    this.modalInstance = this.$modal.open({
      template: `<div id="${this.photoEditorElemId}"></div>`,
      windowClass: 'photo-editor-beautify-modal',
    });

    this.modalInstance.opened.then(() => {
      this.recordBeautifyEvent(
        this.trackingService.ACTION_BEAUTIFY_OPEN_EDITOR,
        `Context: ${this.uiaLaunchContext}`,
      );
      this.$rootScope.$broadcast('beautify-button:Opened');

    }).finally(() => {
      // Once the modal is opened, launch the photo editor
      const watchForModal = setInterval(() => {
        if (angular.element(`#${this.photoEditorElemId}`).length > 0) {
          clearInterval(watchForModal);
          this.$timeout(() => {
            this.launchEditor(this.imageToLoad);
          });
        }
      }, 4);
    });

    this.modalInstance.result.finally(() => {
      this.modalInstance = null;
      this.$rootScope.$broadcast('beautify-button:Closed');
      try {
        this.editor.dispose();
      } catch (e) {
        this.$log.error('Error when closing PhotoEditorSDK', e);
      }

    });
  }

  private preloadImage(imageUrl) {
    // Prep image for editor
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const src = imageUrl; // UIA image URL

    img.crossOrigin = 'anonymous';

    this.editor = null;

    this.isLoading = false;

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      this.imageToLoad = img;
    };
    img.src = src;
  }

  private launchEditor(image) {
    const container = angular.element('#photo-editor')[0];

    // Get the editor key portion of the config first, since underscore extend() doesn't
    // do a deep extend of objects.
    let sdk;
    switch (this.config) {
      case BeautifyConfig.Instagram:
        sdk = instagramPhotoEditorConfig;
        break;
      case BeautifyConfig.Default:
      default:
        sdk = photoEditorConfig;
    }
    const configEditorKey = _(sdk.editor).extend({ image });
    const editorConfig = _(sdk).extend({
      container,
      editor: configEditorKey,
    });

    // TODO: This should probably always be defined now.
    if (PhotoEditorSDKReactUI) {
      this.editor = new PhotoEditorSDKReactUI(editorConfig);

      this.editor.on('export', (newImage, editor) => {
        this.saveClicked({ newUrl: newImage });

        // Best way to gather the list of edits applied to this image,
        // according to the PhotoEditorSDK docs
        const operationsStack = editor.getSDK().getOperationsStack();
        const editsList = [];
        operationsStack.forEach((op) => editsList.push(op.constructor.identifier));

        this.recordBeautifyEvent(this.trackingService.ACTION_BEAUTIFY_SAVE_IMAGE, `Edits used: ${editsList}`);

        this.$log.info(`Beautify success for "${this.imageUrl}"`);
        this.modalInstance.close();
      });

      this.editor.on('close', () => {
        this.recordBeautifyEvent(this.trackingService.ACTION_BEAUTIFY_CLOSE_EDITOR);
        this.$log.info(`Beautify closed for "${this.imageUrl}"`);
        this.modalInstance.close();
      });
    }
  }

  private recordBeautifyEvent(action: TRACKING_ACTION, label?: string) {
    this.trackingService.recordEvent(
      this.trackingService.CATEGORY_BEAUTIFY,
      action,
      label,
    );
  }
}

angular.module('curalate').component('photoEditorBeautifyButton', {
  bindings: {
    disabled: '<ngDisabled',
    imageUrl: '<',
    fileId: '<?',
    saveClicked: '&',
    beautifyClickedCallback: '&?',
    buttonText: '<',
    uiaLaunchContext: '@',
    config: '<?',
    dataTrackingId: '<?',
    isSchedulerPortEnabled: '<?',
  },
  controller: [
    '$log',
    '$modal',
    '$q',
    '$rootScope',
    '$timeout',
    '$window',
    'ImageService',
    'TrackingService',
    PhotoEditorBeautifyButton,
  ],
  template:
    `<with-unison>
      <button
        ng-if="$ctrl.config === $ctrl.BeautifyConfig.Instagram"
        ng-class="$ctrl.isSchedulerPortEnabled ? 'cu-button-link-caption-primary' : 'cu-button-link-primary'"
        ng-click="$ctrl.beautifyClicked()"
        type="button"
        ng-disabled="$ctrl.isLoading || $ctrl.disabled"
        data-tracking-id="{{ ::$ctrl.dataTrackingId }}"
        data-testid="{{ ::$ctrl.dataTrackingId }}"
      >
        {{ $ctrl.buttonText }}
      </button>
      <beautify-action-button
        ng-if="$ctrl.config !== $ctrl.BeautifyConfig.Instagram"
        is-disabled="$ctrl.isLoading || $ctrl.disabled"
        on-click="$ctrl.beautifyClicked()"
      ></beautify-action-button>
    </with-unison>`,
});
